import { useState, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import fetchJson from '@atlassian/jira-issue-reliability-fetch/src/issue-view-as-json.tsx';
import type { FieldConfiguration } from '@atlassian/jira-issue-field-base/src/services/field-config-service/types.tsx';
import {
	useIsIssueOfIncidentsPractice,
	useIsIssueOfPostIncidentReviewPractice,
} from '@atlassian/jira-issue-field-servicedesk-practices/src/services/use-practices-field-value/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import type {
	AutocompleteReturnType,
	OptionsType,
	RestResult,
	ServiceDetails,
	ValueType,
} from '../../common/types/index.tsx';

export const transform = (details: ServiceDetails): OptionsType<ServiceDetails> => {
	const rawData: ValueType<ServiceDetails> = {
		id: details.id,
		name: details.name,
		data: details,
	};

	return {
		value: details.id,
		label: details.name,
		rawData,
	};
};

export const performSearch = <T,>(
	query: string,
	values: ValueType<T>[],
	autoCompleteUrl?: string | null,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	setOptions: (arg1: OptionsType<ServiceDetails>[]) => void,
	setIsLoading: (arg1: boolean) => void,
): Promise<void> => {
	if (autoCompleteUrl == null) {
		return Promise.resolve();
	}

	setIsLoading(true);
	const existingIds = values.map((value) => value.id);

	return fetchJson(
		`${autoCompleteUrl}${query}`,
		{},
		'fields/service-entity/src/services/use-auto-complete',
	)
		.then((result: RestResult) => {
			const options = result.services
				.filter((service) => existingIds.indexOf(service.id) === -1)
				.map(transform);
			setOptions(options);
			setIsLoading(false);
		})
		.catch(() => {
			setOptions([]);
			setIsLoading(false);
		});
};

export const useNewIsIssueOfIncidentsPractice = functionWithCondition(
	() => fg('agni-customer0-filter-affected-services'),
	useIsIssueOfIncidentsPractice,
	() => false,
);

export const useNewIsIssueOfPostIncidentReviewPractice = functionWithCondition(
	() => fg('agni-customer0-filter-affected-services'),
	useIsIssueOfPostIncidentReviewPractice,
	() => false,
);

export const useAutoComplete = <T,>(
	values: ValueType<T>[],
	fieldConfig: FieldConfiguration<undefined> | null,
): AutocompleteReturnType<ServiceDetails> => {
	const [options, setOptions] = useState<OptionsType<ServiceDetails>[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const isIssueOfIncidentPractice = useNewIsIssueOfIncidentsPractice();
	const isIssueOfPostIncidentReviewPractice = useNewIsIssueOfPostIncidentReviewPractice();

	let autoCompleteUrl = fieldConfig ? fieldConfig.autoCompleteUrl : null;

	if (autoCompleteUrl && fg('agni-customer0-filter-affected-services')) {
		let queryParamsToAppend = '';

		if (isIssueOfIncidentPractice) {
			queryParamsToAppend = 'propertiesFilter=[{"key":"isIncidentEligible","value":"True"}]';
		} else if (isIssueOfPostIncidentReviewPractice) {
			queryParamsToAppend = 'propertiesFilter=[{"key":"isPirEligible","value":"True"}]';
		}

		if (queryParamsToAppend) {
			const splittedURL = autoCompleteUrl.split('?');
			autoCompleteUrl =
				splittedURL.length > 1
					? `${splittedURL[0]}?${queryParamsToAppend}&${splittedURL[1]}`
					: `${splittedURL[0]}?${queryParamsToAppend}`;
		}
	}

	const onInputChange = useCallback(
		(query: string) => {
			performSearch(query, values, autoCompleteUrl, setOptions, setIsLoading);
		},
		[values, autoCompleteUrl],
	);

	const debouncedOnInputChange = useRef(debounce(onInputChange, 400));

	// Initial search
	// Not using onMenuOpen here because onMenuOpen is being called whenever input changes
	// Details https://atlassian.slack.com/archives/CFHSME8EB/p1629070846357700
	// TODO: add back onMenuOpen once @atlaskit/select is upgraded
	useEffect(() => {
		performSearch('', values, autoCompleteUrl, setOptions, setIsLoading);
	}, [values, autoCompleteUrl]);

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const onMenuClose = () => {};

	return {
		selectProps: {
			isLoading,
			// Prevent showing cached options while loading, to reduce the risk of duplicates
			options: isLoading ? [] : options,
			onInputChange: debouncedOnInputChange.current,
			onMenuClose,
		},
	};
};
